import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BookOpen, ArrowLeft, Award, Clock, FileText } from "lucide-react";
import { Link, useRoute } from "wouter";
import { trpc } from "@/lib/trpc";
import { usePageMeta } from "@/hooks/usePageMeta";

const LEVEL_META: Record<number, { title: string; description: string }> = {
  1: {
    title: "Biz-AI検定 Associate シラバス | 試験範囲・出題形式",
    description:
      "Biz-AI検定 Associateのシラバス詳細。AIの基礎・ビジネス活用・倫理・セキュリティを問うオンライン資格試験。受験スケジュールや出題形式もこちらで確認できます。",
  },
  2: {
    title: "Biz-AI検定 Specialist シラバス | 試験範囲・出題形式",
    description:
      "Biz-AI検定 Specialistのシラバス詳細。高度なAI活用・実装・プロジェクト運営を問う上位資格。受験スケジュールや出題形式もこちらで確認できます。",
  },
};

export default function Syllabus() {
  const [, params] = useRoute("/syllabus/:levelId");
  const levelId = params?.levelId ? Number(params.levelId) : null;
  const { user, isAuthenticated } = useAuth();

  const meta = levelId ? LEVEL_META[levelId] : undefined;
  usePageMeta({
    title: meta?.title ?? "シラバス一覧 | Biz-AI検定",
    description:
      meta?.description ??
      "Biz-AI検定のシラバス一覧。AssociateとSpecialistの試験範囲・出題形式・受験スケジュールをご確認ください。",
  });

  const { data: levels } = trpc.examLevels.list.useQuery();
  const { data: level } = trpc.examLevels.get.useQuery(
    { id: levelId! },
    { enabled: !!levelId }
  );
  const { data: sessions } = trpc.examSessions.byLevel.useQuery(
    { levelId: levelId! },
    { enabled: !!levelId }
  );

  if (!levelId) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
        <header className="bg-white/80 backdrop-blur-sm border-b border-purple-100 sticky top-0 z-50">
          <div className="container mx-auto px-4 py-4 flex justify-between items-center">
            <Link href="/">
              <h1 className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent cursor-pointer">
                Biz-AI
              </h1>
            </Link>
          </div>
        </header>

        <section className="py-16 px-4">
          <div className="container mx-auto max-w-4xl">
            <h2 className="text-3xl font-bold text-center mb-12 text-gray-800">シラバス一覧</h2>
            <div className="grid md:grid-cols-2 gap-8">
              {levels?.map((lvl) => (
                <Link key={lvl.id} href={`/syllabus/${lvl.id}`}>
                  <Card className="border-2 border-indigo-200 hover:border-indigo-400 transition-colors cursor-pointer h-full">
                    <CardHeader>
                      <CardTitle className="flex items-center gap-2 text-indigo-600">
                        <BookOpen className="w-6 h-6" />
                        {lvl.displayName || lvl.name}
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-gray-600">{lvl.description || "シラバスを確認する"}</p>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50">
      <header className="bg-white/80 backdrop-blur-sm border-b border-purple-100 sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4 flex justify-between items-center">
          <Link href="/">
            <h1 className="text-2xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent cursor-pointer">
              Biz-AI
            </h1>
          </Link>
          <nav className="flex gap-4 items-center">
            {isAuthenticated && (
              <Link href="/register">
                <Button className="bg-gradient-to-r from-indigo-600 to-purple-600">受験申込</Button>
              </Link>
            )}
          </nav>
        </div>
      </header>

      <div className="container mx-auto px-4 py-8 max-w-4xl">
        <Link href="/syllabus">
          <Button variant="ghost" className="mb-6 text-gray-600 hover:text-gray-800">
            <ArrowLeft className="w-4 h-4 mr-2" />
            シラバス一覧に戻る
          </Button>
        </Link>

        {level && (
          <>
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-gray-800 mb-2">
                {level.displayName || level.name} シラバス
              </h2>
              {level.description && (
                <p className="text-lg text-gray-600">{level.description}</p>
              )}
            </div>

            {sessions && sessions.length > 0 && (
              <Card className="mb-8">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-indigo-600">
                    <FileText className="w-5 h-5" />
                    試験概要
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid sm:grid-cols-3 gap-4">
                    {sessions.map((s) => (
                      <div key={s.id} className="bg-gray-50 rounded-lg p-4">
                        {sessions.length > 1 && (
                          <div className="font-medium text-sm text-gray-800 mb-2">{s.displayName || s.name}</div>
                        )}
                        <div className="space-y-1 text-sm text-gray-600">
                          <p className="flex items-center gap-1">
                            <Clock className="w-4 h-4" />
                            試験時間: {s.duration || "-"}分
                          </p>
                          <p className="flex items-center gap-1">
                            <Award className="w-4 h-4" />
                            合格基準: {s.passingScore || "-"}%
                          </p>
                          <p>問題数: {s.questionCount || "-"}問</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {level.syllabusContent ? (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2 text-indigo-600">
                    <BookOpen className="w-5 h-5" />
                    学習内容
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="prose prose-indigo max-w-none whitespace-pre-wrap text-gray-700 leading-relaxed">
                    {level.syllabusContent}
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent className="py-12 text-center text-gray-500">
                  <BookOpen className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>シラバスはまだ登録されていません。</p>
                </CardContent>
              </Card>
            )}

            {level.prerequisiteLevelId && (
              <Card className="mt-6 border-amber-200 bg-amber-50">
                <CardContent className="py-4">
                  <p className="text-amber-800 text-sm">
                    ※ この試験を受けるには、前提となる資格の合格が必要です。
                    <Link href={`/syllabus/${level.prerequisiteLevelId}`}>
                      <span className="underline ml-1 cursor-pointer">前提資格のシラバスを見る</span>
                    </Link>
                  </p>
                </CardContent>
              </Card>
            )}
          </>
        )}
      </div>

      <footer className="py-6 text-center text-xs text-gray-400">
        © 2026 合同会社AIM All Rights Reserved.
      </footer>
    </div>
  );
}
